import { useCallback, useEffect, useRef, useState } from 'react'
import './Tour.css'

const STEPS = [
  {
    target: 'add-school',
    title: 'Add Your Target Schools',
    description: 'Search our database of 806 college baseball programs and add them to your pipeline.',
  },
  {
    target: 'col-researching',
    title: 'Track Your Pipeline',
    description: 'Schools move through these columns as your recruiting process progresses — from Researching all the way to Committed.',
  },
  {
    target: 'first-card',
    title: 'Drag to Update Status',
    description: 'Drag any school card to a new column to update its recruiting status instantly.',
  },
  {
    target: 'first-card',
    title: 'Click for Full Details',
    description: 'Click into any school card to see coaches, add notes, track contact level, and see the distance from home.',
  },
  {
    target: 'nav-mypage',
    title: 'Build Your Profile',
    description: 'Create your public athlete profile page — share the link directly with coaches.',
  },
  {
    target: 'nav-schools',
    title: 'Browse All Programs',
    description: 'Explore all 806 NCAA and JUCO baseball programs, filter by division and distance, and add them to your pipeline.',
  },
]

// Find the first element with this data-tour that has a non-zero bounding rect
// (handles cases where an element exists in DOM but is hidden via CSS)
function findVisible(target) {
  const els = document.querySelectorAll(`[data-tour="${target}"]`)
  for (const el of els) {
    const r = el.getBoundingClientRect()
    if (r.width > 0 || r.height > 0) return el
  }
  return null
}

export default function Tour({ active, onComplete }) {
  const [activeSteps, setActiveSteps] = useState([])
  const [stepIdx, setStepIdx]         = useState(0)
  const [rect, setRect]               = useState(null)
  const [tooltipStyle, setTooltipStyle] = useState({})
  const spotlitEl  = useRef(null)
  const tooltipRef = useRef(null)

  const clearSpotlight = useCallback(() => {
    if (spotlitEl.current) {
      spotlitEl.current.classList.remove('tour-spotlight')
      spotlitEl.current = null
    }
  }, [])

  const finish = useCallback(() => {
    clearSpotlight()
    onComplete()
  }, [clearSpotlight, onComplete])

  // When tour activates, build the list of steps whose targets exist in the DOM
  useEffect(() => {
    if (!active) {
      clearSpotlight()
      setActiveSteps([])
      return
    }
    const available = STEPS.filter(s => !!findVisible(s.target))
    setActiveSteps(available)
    setStepIdx(0)
  }, [active, clearSpotlight])

  // Apply spotlight whenever the current step changes
  useEffect(() => {
    if (!active || activeSteps.length === 0) return
    clearSpotlight()

    const step = activeSteps[stepIdx]
    if (!step) return

    const el = findVisible(step.target)
    if (!el) return

    el.classList.add('tour-spotlight')
    spotlitEl.current = el
    setRect(el.getBoundingClientRect())
  }, [stepIdx, activeSteps, active, clearSpotlight])

  // Recompute rect on window resize
  useEffect(() => {
    if (!active) return
    function onResize() {
      if (spotlitEl.current) setRect(spotlitEl.current.getBoundingClientRect())
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [active])

  // Position the tooltip next to the spotlight target
  useEffect(() => {
    if (!rect || !tooltipRef.current) return
    const tw     = tooltipRef.current.offsetWidth  || 320
    const th     = tooltipRef.current.offsetHeight || 140
    const margin = 14
    const vw     = window.innerWidth
    const vh     = window.innerHeight

    const spaceBelow = vh - rect.bottom - margin
    const spaceAbove = rect.top - margin

    let top  = spaceBelow >= th ? rect.bottom + margin
             : spaceAbove >= th ? rect.top - th - margin
             : rect.bottom + margin          // fallback: below, clamped later

    let left = Math.max(8, Math.min(vw - tw - 8, rect.left))
    top      = Math.max(8, Math.min(vh - th - 8, top))

    setTooltipStyle({ top: Math.round(top), left: Math.round(left) })
  }, [rect])

  if (!active || activeSteps.length === 0) return null

  const step      = activeSteps[stepIdx]
  const isLast    = stepIdx === activeSteps.length - 1
  const total     = activeSteps.length
  const current   = stepIdx + 1

  return (
    <>
      {/* Transparent overlay catches clicks outside tooltip */}
      <div className="tour-overlay" onClick={finish} />

      <div className="tour-tooltip" ref={tooltipRef} style={tooltipStyle}>
        <div className="tour-top">
          <span className="tour-counter">{current} of {total}</span>
        </div>
        <h3 className="tour-title">{step.title}</h3>
        <p className="tour-desc">{step.description}</p>
        <div className="tour-footer">
          <button className="tour-skip" onClick={finish}>Skip Tour</button>
          <button
            className="tour-next"
            onClick={() => isLast ? finish() : setStepIdx(i => i + 1)}
          >
            {isLast ? 'Done ✓' : 'Next →'}
          </button>
        </div>
      </div>
    </>
  )
}
